import React, { useEffect, useMemo, useState } from 'react'
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, Select, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { marketingService } from '../../services/marketing'
import type {
  MarketingClippingCategory,
  MarketingClippingVehicle,
  MarketingNewsAlert,
  PromoteNewsAlertPayload,
} from '../../types/marketing'

const MODULE_NAME = 'marketing_alertas'
const PERM_LOADING: React.ReactNode = null

type CatalogKind = 'veiculo' | 'categoria'

interface AlertaPromoverModalProps {
  alert: MarketingNewsAlert
  veiculos: MarketingClippingVehicle[]
  categorias: MarketingClippingCategory[]
  userId?: string | null
  onClose: () => void
  onPromoted: (payload: {
    clippingId: string
    alert: MarketingNewsAlert
  }) => void
  onCatalogCreated: (kind: CatalogKind, item: MarketingClippingVehicle | MarketingClippingCategory) => void
}

function toDateInput(value: string | null | undefined): string {
  if (!value) return new Date().toISOString().slice(0, 10)
  return value.slice(0, 10)
}

const AlertaPromoverModal: React.FC<AlertaPromoverModalProps> = ({
  alert,
  veiculos,
  categorias,
  userId,
  onClose,
  onPromoted,
  onCatalogCreated,
}) => {
  const [title, setTitle] = useState(alert.title)
  const [clippingDate, setClippingDate] = useState(toDateInput(alert.published_at))
  const [categoryId, setCategoryId] = useState(alert.analysis?.suggested_category_id || '')
  const [vehicleId, setVehicleId] = useState(alert.search_site?.vehicle_id || '')
  const [link, setLink] = useState(alert.url)
  const [notes, setNotes] = useState(
    alert.analysis?.summary?.trim() || alert.snippet?.trim() || ''
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const [catalogKind, setCatalogKind] = useState<CatalogKind | null>(null)
  const [catalogName, setCatalogName] = useState('')
  const [catalogSaving, setCatalogSaving] = useState(false)
  const [catalogError, setCatalogError] = useState<string | null>(null)

  useEffect(() => {
    setTitle(alert.title)
    setClippingDate(toDateInput(alert.published_at))
    setCategoryId(alert.analysis?.suggested_category_id || '')
    setVehicleId(alert.search_site?.vehicle_id || '')
    setLink(alert.url)
    setNotes(alert.analysis?.summary?.trim() || alert.snippet?.trim() || '')
    setError(null)
    setValidationErrors({})
  }, [alert])

  const activeVeiculos = useMemo(() => veiculos.filter((v) => v.is_active), [veiculos])
  const activeCategorias = useMemo(() => categorias.filter((c) => c.is_active), [categorias])

  const validate = (): boolean => {
    const errors: Record<string, string> = {}
    if (!title.trim()) errors.title = 'Informe o título'
    if (!clippingDate) errors.clipping_date = 'Informe a data'
    if (!categoryId) errors.category_id = 'Selecione a categoria'
    if (!vehicleId) errors.vehicle_id = 'Selecione o veículo'
    if (!link.trim()) errors.link = 'Informe o link'
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const saveCatalog = async () => {
    const name = catalogName.trim()
    if (!name || !catalogKind) {
      setCatalogError('Informe o nome')
      return
    }
    try {
      setCatalogSaving(true)
      setCatalogError(null)
      if (catalogKind === 'veiculo') {
        const created = await marketingService.createVehicle({ name })
        onCatalogCreated('veiculo', created)
        setVehicleId(created.id)
      } else {
        const created = await marketingService.createCategory({ name })
        onCatalogCreated('categoria', created)
        setCategoryId(created.id)
      }
      setCatalogKind(null)
      setCatalogName('')
    } catch (err: unknown) {
      setCatalogError(err instanceof Error ? err.message : 'Erro ao cadastrar')
    } finally {
      setCatalogSaving(false)
    }
  }

  const save = async () => {
    if (!validate()) return
    const payload: PromoteNewsAlertPayload = {
      alert_id: alert.id,
      title: title.trim(),
      clipping_date: clippingDate,
      category_id: categoryId,
      notes: notes.trim() || null,
      search_config_id: alert.search_config_id,
      created_by: userId ?? null,
      items: [{ vehicle_id: vehicleId, link: link.trim() }],
    }
    try {
      setSaving(true)
      setError(null)
      const result = await marketingService.promoteNewsAlertToClipping(payload)
      onPromoted({ clippingId: result.clipping.id, alert: result.alert })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao marcar alerta como clipping')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
            Marcar como clipping
          </Heading>
          <Button
            onClick={onClose}
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
            disabled={saving}
          >
            <XMarkIcon className="h-4 w-4" />
            Fechar
          </Button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            {alert.analysis?.reason ||
            alert.analysis?.summary ||
            alert.analysis?.relevance_score != null ||
            alert.analysis?.sentiment ||
            alert.analysis?.is_clipping != null ? (
              <div className="bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                  Análise de IA
                </Text>
                <div className="flex flex-wrap gap-3">
                  {alert.analysis?.relevance_score != null && (
                    <span>
                      <span className="font-medium text-gray-800 dark:text-gray-100">Score: </span>
                      {alert.analysis.relevance_score}
                    </span>
                  )}
                  {alert.analysis?.sentiment && (
                    <span>
                      <span className="font-medium text-gray-800 dark:text-gray-100">Sentimento: </span>
                      {alert.analysis.sentiment === 'positivo'
                        ? 'Positivo'
                        : alert.analysis.sentiment === 'negativo'
                          ? 'Negativo'
                          : 'Neutro'}
                    </span>
                  )}
                  {alert.analysis?.is_clipping != null && (
                    <span>
                      <span className="font-medium text-gray-800 dark:text-gray-100">Sugestão: </span>
                      {alert.analysis.is_clipping ? 'É clipping' : 'Não é clipping'}
                    </span>
                  )}
                </div>
                {alert.analysis?.summary && (
                  <p>
                    <span className="font-medium text-gray-800 dark:text-gray-100">Resumo: </span>
                    {alert.analysis.summary}
                  </p>
                )}
                {alert.analysis?.reason && (
                  <p>
                    <span className="font-medium text-gray-800 dark:text-gray-100">Justificativa: </span>
                    {alert.analysis.reason}
                  </p>
                )}
              </div>
            ) : null}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <Text size="sm" className="mb-2">
                  Título <span className="text-red-500">*</span>
                </Text>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Digite o título do clipping..."
                  className={validationErrors.title ? 'border-red-500' : ''}
                />
                {validationErrors.title && (
                  <Text size="sm" className="text-red-500 mt-1">{validationErrors.title}</Text>
                )}
              </div>

              <div>
                <Text size="sm" className="mb-2">
                  Data <span className="text-red-500">*</span>
                </Text>
                <Input
                  type="date"
                  value={clippingDate}
                  onChange={(e) => setClippingDate(e.target.value)}
                  className={validationErrors.clipping_date ? 'border-red-500' : ''}
                />
                {validationErrors.clipping_date && (
                  <Text size="sm" className="text-red-500 mt-1">{validationErrors.clipping_date}</Text>
                )}
              </div>
            </div>

            <div>
              <Text size="sm" className="mb-2">
                Categoria <span className="text-red-500">*</span>
              </Text>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <Select
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    className={validationErrors.category_id ? 'border-red-500' : ''}
                  >
                    <option value="">Selecione...</option>
                    {activeCategorias.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                    {categoryId && !activeCategorias.some((c) => c.id === categoryId) && (
                      <option value={categoryId}>
                        {categorias.find((c) => c.id === categoryId)?.name || 'Categoria inativa'} (inativo)
                      </option>
                    )}
                  </Select>
                </div>
                <PermissionGuard
                  moduleName={MODULE_NAME}
                  permissionType="insert"
                  fallback={null}
                  loadingComponent={PERM_LOADING}
                >
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCatalogKind('categoria')
                      setCatalogName('')
                      setCatalogError(null)
                    }}
                    title="Cadastrar categoria"
                    className="shrink-0 px-2"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </Button>
                </PermissionGuard>
              </div>
              {validationErrors.category_id && (
                <Text size="sm" className="text-red-500 mt-1">{validationErrors.category_id}</Text>
              )}
            </div>

            <div>
              <Text size="sm" className="mb-2">
                Veículo e link <span className="text-red-500">*</span>
              </Text>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1.4fr] gap-2 items-end">
                <div>
                  <Text size="xs" className="mb-1 text-gray-500 dark:text-gray-400">
                    Nome do veículo
                  </Text>
                  <Select
                    value={vehicleId}
                    onChange={(e) => setVehicleId(e.target.value)}
                    className={validationErrors.vehicle_id ? 'border-red-500' : ''}
                  >
                    <option value="">Selecione...</option>
                    {activeVeiculos.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                    {vehicleId && !activeVeiculos.some((v) => v.id === vehicleId) && (
                      <option value={vehicleId}>
                        {veiculos.find((v) => v.id === vehicleId)?.name || 'Veículo inativo'} (inativo)
                      </option>
                    )}
                  </Select>
                </div>
                <PermissionGuard
                  moduleName={MODULE_NAME}
                  permissionType="insert"
                  fallback={null}
                  loadingComponent={PERM_LOADING}
                >
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCatalogKind('veiculo')
                      setCatalogName('')
                      setCatalogError(null)
                    }}
                    title="Cadastrar veículo"
                    className="shrink-0 px-2 mb-0.5"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </Button>
                </PermissionGuard>
                <div>
                  <Text size="xs" className="mb-1 text-gray-500 dark:text-gray-400">
                    Link
                  </Text>
                  <Input
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    className={validationErrors.link ? 'border-red-500' : ''}
                  />
                </div>
              </div>
              {(validationErrors.vehicle_id || validationErrors.link) && (
                <Text size="sm" className="text-red-500 mt-1">
                  {validationErrors.vehicle_id || validationErrors.link}
                </Text>
              )}
            </div>

            <div>
              <Text size="sm" className="mb-2">Observações</Text>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                placeholder="Observações opcionais..."
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-6 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="solid" onClick={save} disabled={saving}>
            {saving ? 'Salvando...' : 'Marcar como clipping'}
          </Button>
        </div>
      </div>

      {catalogKind && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                {catalogKind === 'veiculo' ? 'Novo veículo' : 'Nova categoria'}
              </Heading>
              <Button
                onClick={() => setCatalogKind(null)}
                variant="outline"
                size="sm"
                disabled={catalogSaving}
              >
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 space-y-3">
              {catalogError && (
                <Text size="sm" className="text-red-500">{catalogError}</Text>
              )}
              <div>
                <Text size="sm" className="mb-2">Nome</Text>
                <Input
                  value={catalogName}
                  onChange={(e) => setCatalogName(e.target.value)}
                  placeholder="Nome"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={() => setCatalogKind(null)} disabled={catalogSaving}>
                Cancelar
              </Button>
              <Button variant="solid" onClick={saveCatalog} disabled={catalogSaving || !catalogName.trim()}>
                {catalogSaving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AlertaPromoverModal
